import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Paper,
  Chip,
  useMediaQuery,
  Snackbar,
  Alert,
} from "@mui/material";
import CheckIcon from '@mui/icons-material/Check';
import SubscriptionService from "../services/SubscriptionService";
import MyAccountService from "../services/MyAccountService";
import ServicesService from "../services/ServicesService";
import { Text, toaster } from "evergreen-ui";
import { priceFormate } from "../utils/extentions";
import { errorMessage } from "../shared/utils/validators";

const SAMPLE_PLANS = [
  {
    id: 'plan_basic',
    name: 'Basic',
    description: 'Perfect for getting started',
    amount: 499,
    duration: 'monthly',
    features: [
      { name: 'Up to 50 products' },
      { name: 'Basic analytics' },
      { name: 'Email support' },
    ],
  },
  {
    id: 'plan_pro',
    name: 'Pro',
    description: 'Best for growing businesses',
    amount: 999,
    duration: 'monthly',
    features: [
      { name: 'Unlimited products' },
      { name: 'Advanced analytics' },
      { name: 'Custom domain' },
      { name: 'Priority support' },
    ],
  },
  {
    id: 'plan_enterprise',
    name: 'Enterprise',
    description: 'For large scale businesses',
    amount: 9999,
    duration: 'yearly',
    features: [
      { name: 'All Pro features' },
      { name: 'Dedicated account manager' },
      { name: 'Custom integrations' },
      { name: '24/7 support' },
    ],
  },
];


const PlansView = () => {
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isWaiting, setIsWaiting] = useState(false);
  const [account, setAccount] = useState(null);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [subscribedId, setSubscribedId] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [adminSettings, setAdminSettings] = useState(null);
    const [snackbar, setSnackbar] = useState({
      open: false,
      message: "",
      severity: "success",
    });
  const isMobile = useMediaQuery('(max-width:600px)');
    
  // Dummy currency and helpers for demo
  const currencyText = '₹';
  const DURATION_TYPE = { MONTHLY: 'monthly', YEARLY: 'yearly', WEEKLY: 'weekly'};
  function getAmountPer(duration) {
    if (DURATION_TYPE.MONTHLY === duration) return ' / month';
    if (DURATION_TYPE.YEARLY === duration) return ' / year';
    if (DURATION_TYPE.WEEKLY === duration) return ' / week';
    return null;
  }
  function getProperAmount(amount, duration) {
    if (DURATION_TYPE.YEARLY === duration) return amount / 12;
    return amount;
  }

  async function fetchAll() {
      setLoading(true);
      const plans = await SubscriptionService.getAllPlans();
      console.log('plans', plans);
      const account = await MyAccountService.fetch();
      console.log('acc', account);
      console.log('subbb', account?.getSubscription());
      
      setPlans(plans);
      setAccount(account);
      setSelectedPlanId(account.getSubscription()?.getPlanId());
      setSubscribedId(account.getSubscription()?.getSubscriptionId());
      setAdminSettings(await ServicesService.getAdminSettings());
      setLoading(false);
    }

  // Fetch plans and account info
  useEffect(() => {
    fetchAll();
  }, []);
  function renderSnackbar() {
      return (
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      );
    }

  async function handleSubscription(planId) {
    setLoading(true);
    try {
      await SubscriptionService.createSubscription({ planId });
      setSnackbar({
        open: true,
        message: "Subscribed successfully!",
        severity: "success",
      });
    } catch (error) {
       const message = errorMessage(error);
      if (message) {
        setSnackbar({
          open: true,
          message: `${message}`,
          severity: "error",
        });
      } else {
        setSnackbar({
          open: true,
          message: "Something went wrong",
          severity: "error",
        });
      }
    }
    setTimeout(() => {
      setSelectedPlanId(planId);
      setLoading(false);
    }, 1000);
  }
  async function handleCancelSubscription(id) {
    setLoading(true);
    try {
      await SubscriptionService.cancelSubscription(id);
      toaster.success('Subscription Cancelled Succesfully');
      fetchAll();
    } catch (error) {
      console.log('Error', error);
    }
  }

  function renderDeleteDialog() {
    return (
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Cancel Subscription</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to cancel your subscription?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>No</Button>
          <Button color="error" onClick={() => handleCancelSubscription(subscribedId)}>Yes, Cancel</Button>
        </DialogActions>
      </Dialog>
    );
  }

  function renderPlanCard(plan) {
    const isSelected = selectedPlanId === plan.getPlanId();
    return (
      <Paper
        key={plan.getPlanId()}
        elevation={isSelected ? 8 : 2}
        sx={{
          p: 3,
          borderRadius: 3,
          border: isSelected ? '2px solid #1976d2' : '1px solid #e0e0e0',
          minWidth: 260,
          maxWidth: 340,
          width: '100%',
          mb: isMobile ? 2 : 0,
          position: 'relative',
          bgcolor: isSelected ? '#f0f7ff' : '#fff',
        }}
      >
        {isSelected && (
          <Chip label="Current Plan" color="primary" size="small" icon={<CheckIcon />} sx={{ position: 'absolute', top: 16, right: 16 }} />
        )}
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{plan.getPlanName()}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{plan.getDescription()}</Typography>
        <Text>{adminSettings?.getTrialPeriod() != null && `Enjoy trial period of ${adminSettings.getTrialPeriod()} days`}</Text>
        <Typography variant="h4" sx={{ color: '#1976d2', fontWeight: 700 }}>
          {`${priceFormate(getProperAmount(plan.getAmount(), plan.getDuration()))}`}
          <Typography component="span" variant="body2" color="text.secondary">
            {getAmountPer(plan.getDuration())}
          </Typography>
        </Typography>
        {isSelected ? (
          <Button variant="outlined" color="error" fullWidth onClick={() => setDeleteDialog(true)}>
            Cancel Plan
          </Button>
        ) : (
          <Button variant="contained" color="primary" fullWidth onClick={() => handleSubscription(plan.getPlanId(), plan.getAmount(), plan.getApplyCount())}>
            Select Plan
          </Button>
        )}
        <Box sx={{ mt: 2, mb: 2 }}>
          {plan.getFeatures().map((f, idx) => (
            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <CheckIcon fontSize="small" color="success" sx={{ mr: 1 }} />
              <Typography variant="body2">{f.name}</Typography>
            </Box>
          ))}
        </Box>
      </Paper>
    );
  }

  if (loading || !plans) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: { xs: 1, md: 4 }, bgcolor: '#f7f8fa', minHeight: '100vh' }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: { md: 5, xs: 6 }, textAlign: 'center' }}>
        Explore Our Plans & Find Your Perfect Fit!
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 3,
          justifyContent: 'center',
          alignItems: { xs: 'center', md: 'flex-start' },
          mb: 4,
        }}
      >
        {plans?.map((p) => renderPlanCard(p))}
      </Box>
      {renderDeleteDialog()}
      {renderSnackbar()}
    </Box>
  );
};

export default PlansView;
